import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    effect,
    inject,
    input,
    signal,
    AfterViewInit,
    PLATFORM_ID,
    SimpleChanges,
    OnChanges,
    viewChildren,
    ElementRef,
    WritableSignal,
    DestroyRef,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';

import { ArtGalleryService } from './art-gallery.service';
import { environment } from '../../../environments/environment';
import { slideDown, slideUp } from '../../animations';
import { AppService } from '../../app.service';
import { CommonModule, DOCUMENT, isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { debounceTime, distinctUntilChanged, filter, fromEvent, map, startWith } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'iea-app-gallery',
    templateUrl: './gallery.component.html',
    styleUrls: ['./gallery.component.scss'],
    animations: [...slideUp, ...slideDown],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, RouterLink, InfiniteScrollDirective, NgOptimizedImage],
    host: { class: 'container' },
    standalone: true,
})
export default class GalleryComponent implements OnChanges {
    infiniteScrollDistance = environment.gallery.infiniteScrollDistance;
    private readonly doc = inject(DOCUMENT);
    private readonly injector = inject(Injector);
    public readonly appService = inject(AppService);
    public readonly gallery = inject(ArtGalleryService);
    private readonly router = inject(Router);

    viewColumns = viewChildren<ElementRef<HTMLDivElement>>('column');
    galleryColumns = signal([signal([]), signal([]), signal([])]);

    category = input.required<string>();
    filter = input<string | undefined>(undefined);

    // browser id
    private readonly platformId = inject(PLATFORM_ID);
    isBrowser = isPlatformBrowser(this.platformId);

    observeElementWithId = (id: string, callback: (element: HTMLElement) => void): MutationObserver => {
        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                // Check for added nodes
                mutation.addedNodes.forEach((node) => {
                    if (node instanceof HTMLElement && node.id === id) {
                        callback(node);
                        // Stop observing if you only care about the first occurrence
                        observer.disconnect();
                    }
                });
            }
        });

        // Start observing the entire document for child node additions
        observer.observe(this.doc.body, { childList: true, subtree: true });

        return observer; // Return the observer in case you want to disconnect it manually later
    };
    destroyRef = inject(DestroyRef);
    hostElement = inject(ElementRef);
    constructor() {
        this.scrollToBoardInState();
        if (!this.isBrowser) return;
        effect(
            () => {
                const all = this.gallery.all();
                const columnsEl = this.viewColumns();
                if (columnsEl.length === 0) return;
                const columns = this.galleryColumns();

                this.fillGalleryByColumns(all, columns, Array.from(columnsEl));
            },
            { allowSignalWrites: true, injector: this.injector }
        );

        this.reRenderOnResize();
    }

    private scrollToBoardInState() {
        this.router.events
            .pipe(
                filter((e) => this.isBrowser && e instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((e) => {
                const state = this.router.getCurrentNavigation()?.extras.state;
                if (!state) return;
                const boardElement = this.doc.getElementById(state?.board?.slug);
                if (boardElement) return boardElement.scrollIntoView({ behavior: 'smooth' });
                this.observeElementWithId(state?.board?.slug, (element) => {
                    // setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    element.classList.add('active');
                    setTimeout(() => element.classList.remove('active'), 1000);
                    // }, this.gallery.all().length * 75);
                });
            });
    }
    private reRenderOnResize() {
        if (this.isBrowser) {
            fromEvent(this.doc.body, 'resize')
                .pipe(
                    debounceTime(150),
                    startWith(this.doc.body.clientWidth),
                    map(() => this.doc.body.clientWidth),
                    distinctUntilChanged()
                )
                .subscribe((width) => {
                    const cols = width > 870 ? 3 : width > 480 ? 2 : 1;
                    if (this.galleryColumns().length === cols) return;
                    const newCols = new Array(cols).fill(0).map(() => signal([]));
                    this.galleryColumns.set(newCols);
                });
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.category || changes.filter) {
            const cat = this.category();
            const f = this.filter();
            const shouldReset = this.gallery.category !== cat || this.gallery.filter !== f;
            if (!shouldReset) return this.gallery.rerender();
            if (this.gallery.category !== cat) this.gallery.category = cat;
            if (this.gallery.filter !== f) this.gallery.change_filter(f);
            this.galleryColumns.update((c) => {
                c.forEach((col) => col.set([]));
                return c.slice();
            });
            this.gallery.resetAndGetItems();
        }
    }
    async fillGalleryByColumns(all: any[], galleryColumns: WritableSignal<any>[], columnsEl: ElementRef<HTMLDivElement>[]): Promise<void> {
        all ??= [];
        const columns = columnsEl.map((c) => c.nativeElement).map((c, i) => ({ i, w: c.clientWidth, h: c.clientHeight || 0 }));
        const sortColumns = (cols) => cols.sort((a, b) => a.h - b.h);
        const findMinHeightColIdx = () => sortColumns(columns.slice())[0].i;

        for (let index = 0; index < all.length; index++) {
            const artBoard = all[index];
            if (artBoard.rerendered === true) continue;
            const minHeightColIdx = findMinHeightColIdx();
            const { width, height, path } = artBoard.mediumImage ?? artBoard.largeImage;
            artBoard.src = path;
            artBoard.width = width;
            artBoard.height = height || 1;
            // calculate the aspect ratio of the image
            const aspectRatio = width / artBoard.height;
            const col = galleryColumns[minHeightColIdx];
            col.update((c) => {
                c.push(artBoard);
                return c.slice();
            });
            // calculate the height of the image based on the width of the column
            columns[minHeightColIdx].h += columns[minHeightColIdx].w / aspectRatio;

            artBoard.rerendered = true;
        }

        const tallestColumn = sortColumns(columns.slice()).pop();
        this.hostElement.nativeElement.style.height = `${tallestColumn.h + 100}px`;
    }

    async onScrollDown() {
        if (!this.isBrowser) return;
        if (this.gallery.canGetMore() !== true) return;
        this.gallery.getNextPage();
    }
}
